
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';

export default function CustomerTerms() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
      <Navbar />

      <div className="container mx-auto px-6 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-8">Customer Terms & Conditions</h1>

            <div className="text-gray-600 space-y-8">
              <div>
                <p className="text-sm text-gray-500 mb-4">Last updated: January 2024</p>
                <p className="text-lg mb-4">
                  Welcome to Baainearme (BNM). These Terms and Conditions govern your use of our customer services. By using our platform, you agree to these terms.
                </p>
              </div>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Service Overview</h2>
                <p className="mb-4">BNM is a platform that connects customers with verified service partners for various home services including:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>Bathroom cleaning (basic and intense)</li>
                  <li>Dusting</li>
                  <li>Sofa cleaning</li>
                  <li>Carpet cleaning</li>
                  <li>Kitchen utensil cleaning</li>
                  <li>Fan cleaning</li>
                  <li>Window cleaning</li>
                  <li>Cooking and meal preparation</li>
                  <li>Water filter servicing</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Account Registration</h2>
                <div className="space-y-4">
                  <p>To use our services, you must:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Provide accurate and complete information</li>
                    <li>Be at least 18 years old</li>
                    <li>Maintain the security of your account credentials</li>
                    <li>Notify us immediately of any unauthorized access</li>
                    <li>Accept responsibility for all activities under your account</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Booking and Services</h2>
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">Service Booking</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Services must be booked through our app or website</li>
                    <li>All bookings require advance payment or deposit</li>
                    <li>Service availability depends on partner availability</li>
                    <li>You must provide accurate service location and contact details</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-800">Service Execution</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>You must be present or arrange for someone to be present during service</li>
                    <li>Provide safe and accessible working conditions</li>
                    <li>Treat service partners with respect and courtesy</li>
                    <li>Report any issues immediately through the app</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Payment Terms</h2>
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">Pricing and Fees</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>All prices are clearly displayed before booking confirmation</li>
                    <li>Prices may vary based on service type, duration, and location</li>
                    <li>Additional charges may apply for extra services or materials</li>
                    <li>Service partners may charge separately for materials or supplies</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-800">Payment Processing</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Advance payment or deposit is required for all bookings</li>
                    <li>Remaining payment is due upon service completion</li>
                    <li>We accept various payment methods as displayed in the app</li>
                    <li>All transactions are processed securely</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Cancellation and Refunds</h2>
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">Customer Cancellation</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Free cancellation up to 2 hours before scheduled service</li>
                    <li>Cancellations within 2 hours may incur charges</li>
                    <li>No-show customers forfeit advance payment</li>
                    <li>Repeated cancellations may result in account restrictions</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-800">Refund Policy</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Refunds processed within 5-7 business days</li>
                    <li>Partial refunds for partially completed services</li>
                    <li>Full refund for services not provided due to partner no-show</li>
                    <li>Refund disputes resolved through customer support</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Quality Assurance</h2>
                <div className="space-y-4">
                  <p>We strive to ensure high-quality services through:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Thorough partner verification and background checks</li>
                    <li>Regular quality monitoring and feedback collection</li>
                    <li>Customer rating and review system</li>
                    <li>Prompt resolution of quality issues</li>
                    <li>Re-service guarantee for unsatisfactory work</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Liability and Insurance</h2>
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">Our Liability</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>We facilitate connections between customers and service partners</li>
                    <li>Service partners are independent contractors, not our employees</li>
                    <li>Our liability is limited to the service fee paid</li>
                    <li>We maintain insurance coverage for certain service categories</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-800">Customer Responsibility</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Secure valuable items before service begins</li>
                    <li>Report damages or issues immediately</li>
                    <li>Provide accurate information about service requirements</li>
                    <li>Ensure safe working conditions for service partners</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Prohibited Uses</h2>
                <p className="mb-4">You agree not to:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-600">
                  <li>Use the platform for illegal or harmful activities</li>
                  <li>Harass, threaten, or discriminate against service partners</li>
                  <li>Attempt to circumvent our payment system</li>
                  <li>Share account credentials with others</li>
                  <li>Submit false reviews or ratings</li>
                  <li>Use the platform for commercial purposes without permission</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Dispute Resolution</h2>
                <div className="space-y-4">
                  <p>For any disputes or issues:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Contact our customer support team first</li>
                    <li>We will mediate between customers and service partners</li>
                    <li>Unresolved disputes may be subject to arbitration</li>
                    <li>Legal proceedings are subject to Nagpur jurisdiction</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Contact Information</h2>
                <p className="mb-4">For questions about these terms or our services:</p>
                <div className="bg-purple-50 rounded-lg p-6">
                  <div className="space-y-2">
                    <p><strong>Email:</strong> support@baainearme.com</p>
                    <p><strong>Phone:</strong> 93074 19845</p>
                    <p><strong>Address:</strong> BNM Headquarters, Nagpur, Maharashtra, India</p>
                    <p><strong>Hours:</strong> Monday-Saturday, 9 AM - 6 PM</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Changes to Terms</h2>
                <p>
                  We reserve the right to modify these terms at any time. Significant changes will be communicated through email or app notifications. Continued use of our services after changes constitutes acceptance of the updated terms.
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              {/* BNM logo (SVG) — scalable, color via currentColor */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 420 160"
                className="h-10 text-white"   // change to text-purple-600 etc.
                role="img"
                aria-label="BNM · Baainearme"
              >
                <g fill="none" stroke="currentColor" strokeWidth="12" strokeLinecap="round">
                  {/* Left ring */}
                  <circle cx="50" cy="60" r="40" />
                  {/* Vertical divider */}
                  <line x1="110" y1="20" x2="110" y2="120" />
                </g>

                {/* Text block */}
                <g fill="currentColor">
                  {/* BNM */}
                  <text
                    x="140"
                    y="72"
                    fontFamily="Poppins, Inter, Arial, sans-serif"
                    fontWeight="800"
                    fontSize="56"
                  >
                    BNM
                  </text>

                  {/* Baainearme */}
                  <text
                    x="140"
                    y="120"
                    fontFamily="Poppins, Inter, Arial, sans-serif"
                    fontWeight="700"
                    fontSize="32"
                  >
                    Baainearme
                  </text>
                </g>
              </svg>

              <p className="text-gray-400">
                Your trusted platform for on-demand home services
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">For Customers</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/customer" className="hover:text-white cursor-pointer">Book Services</Link></li>
                <li><Link to="/customer-privacy" className="hover:text-white cursor-pointer">Privacy Policy</Link></li>
                <li><Link to="/customer-terms" className="hover:text-white cursor-pointer">Terms & Conditions</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/partner" className="hover:text-white cursor-pointer">Become Partner</Link></li>
                <li><Link to="/about" className="hover:text-white cursor-pointer">About Us</Link></li>
                <li><Link to="/refund-policy" className="hover:text-white cursor-pointer">Refund Policy</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li>📞 93074 19845</li>
                <li>✉ support@baainearme.com</li>
                <li><a href="#" className="hover:text-white cursor-pointer">Help Center</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Baainearme. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
